import React from 'react';
import { 
  Rocket, 
  Cpu, 
  Globe, 
  Zap, 
  ShieldCheck, 
  BarChart, 
  Layers, 
  Sparkles,
  Command,
  FastForward,
  Activity,
  CheckCircle
} from 'lucide-react';
import './BuiltForFuture.css';
import Hero from '../../components/Hero/Hero';
import FAQ from '../../components/FAQ/FAQ';
import CTASection from '../../components/CTA/CTA';
import Footer from '../../components/Footer/Footer';

const services = [
  {
    title: 'Modern Architecture Design',
    description: 'We build systems that are modular, decoupled, and ready for whatevercomes next. Our designs prioritize flexibility and extensibility.',
    features: [
      'Microservices and modular patterns',
      'Event-driven and reactive systems',
      'Scalable data mesh architectures'
    ],
    image: '/images/built-for-future/architecture.jpg'
  },
  {
    title: 'Future-Ready Workflows',
    description: 'Empower your teams with AI-integrated DevOps, automated delivery, and collaborative tools that boost velocity.',
    features: [
      'AI-assisted coding and testing',
      'Platform engineering for scale',
      'Automated documentation and governance'
    ],
    image: '/images/built-for-future/workflows.jpg'
  },
  {
    title: 'Emerging Tech Integration',
    description: 'Seamlessly incorporate Web3, edge computing, and AI/ML capabilities into your existing stack to stay ahead of the curve.',
    features: [
      'Edge-first delivery networks',
      'MLOps pipeline integration',
      'Decentralized identity solutions'
    ],
    image: '/images/built-for-future/tech-integration.jpg'
  }
];

const capabilities = [
  {
    icon: <Sparkles size={24} />,
    title: 'AI-Native Systems',
    text: 'Building intelligence into the core of your applications for predictive insights and automated decisioning.'
  },
  {
    icon: <FastForward size={24} />,
    title: 'Rapid Experimentation',
    text: 'Shorten the loop from idea to production with feature flags, A/B testing, and canary deployments.'
  },
  {
    icon: <Globe size={24} />,
    title: 'Global Distribution',
    text: 'Deploy your assets to the edge to reach users worldwide with sub-10ms latency and high resilience.'
  },
  {
    icon: <Command size={24} />,
    title: 'Infinite Scalability',
    text: 'Serverless and elastic designs that automatically adjust to traffic spikes without manual intervention.'
  }
];

const faqItems = [
  {
    question: 'How do you define "Future-Proof"?',
    answer: 'A future-proof system is one designed with open standards, decoupled components, and clear documentation, allowing it to be easily adapted as business needs and technologies evolve.'
  },
  {
    question: 'Can you modernize our legacy systems?',
    answer: 'Yes. We specialize in incremental modernization strategies like the Strangler Fig pattern, allowing you to migrate to modern architectures without massive upfront risk.'
  },
  {
    question: 'What technologies do you recommend for future growth?',
    answer: 'We typically recommend cloud-native stacks (Kubernetes, Serverless), TypeScript, React/Next.js, and modern data platforms that scale horizontally.'
  }
];

export default function BuiltForFuture() {
  return (
    <article className="built-for-future-page">
      <Hero 
        eyebrow="Visionary Engineering"
        title="Modern Solutions for Tomorrow."
        subtitle="We build digital ecosystems that don't just solve today's problems but are architected for the breakthroughs of the next decade."
        stats={[
          { label: 'Modernity', value: '10/10' },
          { label: 'Growth', value: 'Unbounded' }
        ]}
      />

      <section className="section section-capabilities">
        <div className="section-heading">
          <span className="eyebrow">Innovation DNA</span>
          <h2>Designed for the next era of digital.</h2>
          <p>We leverage the latest advancements in cloud, AI, and distributed systems to give your business a permanent competitive edge.</p>
        </div>
        <div className="capabilities-grid">
          {capabilities.map((cap, i) => (
            <div key={i} className="capability-card">
              <div className="cap-icon">{cap.icon}</div>
              <h3>{cap.title}</h3>
              <p>{cap.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-solutions">
        <div className="solutions-container">
          {services.map((service, index) => (
            <div key={index} className={`solution-row ${index % 2 !== 0 ? 'reverse' : ''}`}>
              <div className="solution-copy">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="feature-list">
                  {service.features.map((f, fi) => (
                    <li key={fi}><CheckCircle size={18} /> {f}</li>
                  ))}
                </ul>
              </div>
              <div className="solution-media">
                <img src={service.image} alt={service.title} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-metrics">
        <div className="metrics-grid">
          <div className="metric-item">
            <strong>100%</strong>
            <span>Cloud Native</span>
          </div>
          <div className="metric-item">
            <strong>AI</strong>
            <span>First Design</span>
          </div>
          <div className="metric-item">
            <strong>Edge</strong>
            <span>Distributed</span>
          </div>
          <div className="metric-item">
            <strong>Modular</strong>
            <span>Architectures</span>
          </div>
        </div>
      </section>

      <section className="section section-faq">
        <div className="section-heading">
          <span className="eyebrow">Future Insights</span>
          <h2>The Path Forward.</h2>
        </div>
        <FAQ items={faqItems} />
      </section>

      <CTASection />
      
      <Footer />
    </article>
  );
}
